import { useState, type ReactNode } from "react";
import {
  Box,
  Button,
  DataTableToolbar,
  DataTableToolbarActions,
  DataTableToolbarLeft,
  Icon,
  OptionPanelRow,
  OptionPanelSection,
  Popover,
  PopoverPopup,
  PopoverTrigger,
  Separator,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  type Omittable,
} from "@salestrak/ui";

import { FilterPanel } from "#app/components/filters/filter-panel";
import { FilterToggle } from "#app/components/filters/filter-toggle";
import { PageHeader, PageHeaderTitle } from "#app/components/page-header";
import { ViewOptionsTrigger } from "#app/components/view-options";
import { ENTITY_KIND_ICON } from "#app/lib/entity-visuals";
import { useIssueFiltersOptional } from "#app/lib/filters/issue-filter-context";
import { scopeIsWorkflowBound, type IssueScope } from "#app/lib/filters/issue-scope";
import { useViewsOptional } from "#app/lib/views/view-context";
import { DashboardRefreshControl } from "#app/widgets/dashboards/dashboard-refresh-control";
import type { WidgetRefresh } from "#app/widgets/dashboards/use-widget-refresh";
import {
  DashboardDateRangePill,
  DashboardFilterToolbar,
  type DashboardFilterToolbarProps,
} from "#app/widgets/dashboards/scope/dashboard-filter-toolbar";
import {
  type DatePresetOption,
  type DatePresetValue,
} from "#app/lib/utils/date-presets";
export const MANAGED_HOME_CONTENT_MAX_WIDTH = 1200;

// Three windows, not the general nine: a managed home is a glance, and its
// window is a visible label on the trigger, so the words have to read as
// an answer to "what am I looking at" — "My week" on the personal home,
// "This week" on a team's. Each carries a one-line description read at the
// moment of choosing: the labels leave real ambiguities open (calendar week
// vs a rolling seven days, month-to-date vs the calendar month, and "All
// time" being the ABSENCE of a date filter rather than a window).
const PERSONAL_HOME_DATE_PRESETS = [
  {
    key: "this-week",
    label: "My week",
    shortLabel: "My week",
    description: "Sunday through Saturday",
  },
  {
    key: "mtd",
    label: "My month",
    shortLabel: "My month",
    description: "The 1st through today",
  },
  {
    key: "all-time",
    label: "All time",
    shortLabel: "All time",
    description: "Everything — no date limit",
  },
] as const satisfies readonly DatePresetOption[];

const TEAM_HOME_DATE_PRESETS = [
  {
    key: "this-week",
    label: "This week",
    shortLabel: "This week",
    description: "Sunday through Saturday",
  },
  {
    key: "mtd",
    label: "This month",
    shortLabel: "This month",
    description: "The 1st through today",
  },
  {
    key: "all-time",
    label: "All time",
    shortLabel: "All time",
    description: "Everything — no date limit",
  },
] as const satisfies readonly DatePresetOption[];

// The managed homes' seat of the ONE dashboard window chip
// (`DashboardDateRangePill` — always-labeled, quiet outline on the view's own
// default window, primary-hued outline + reset-× on a deviation). No
// "Period:" prefix (dropped 2026-08-31): the values already read as windows,
// so the prefix restated what the label says. This wrapper only picks the
// audience's curated preset three and names the view's default window (the
// host's `useDashboardScopeState` seed — all-time on the personal home,
// this-week on a team's) as the single resting key.
function ManagedHomeDateRangeSelect({
  value,
  onChange,
  audience,
  align,
}: {
  value: DatePresetValue;
  onChange: (next: DatePresetValue) => void;
  audience: "personal" | "team";
  /** Popup alignment — matches the chip's seat (left slot vs right actions). */
  align: "start" | "end";
}) {
  const presets =
    audience === "personal" ? PERSONAL_HOME_DATE_PRESETS : TEAM_HOME_DATE_PRESETS;
  const restingKey = audience === "personal" ? "all-time" : "this-week";

  return (
    <DashboardDateRangePill
      value={value}
      onChange={onChange}
      presets={presets}
      restingKeys={[restingKey]}
      align={align}
    />
  );
}

/**
 * The widget host's two header chromes — free dashboards, Team Overview, and
 * My Work are ONE surface concept (`dashboard.tsx`); the ONLY designed
 * difference between them is which of these headers they wear and which
 * toolbar controls it exposes:
 *
 * - `DashboardMastheadChrome` — managed homes (My Work and Team Overview):
 *   the app's standard first-line page header (title left; the labeled
 *   window chip, then the board actions beside it on the right),
 *   or — when the surface is connected to a tab strip — a `DataTableToolbar`
 *   band below the tabs, where every other view's toolbar sits, with the
 *   chip on the LEFT (framing — the band grammar's Group By slot) and the
 *   board actions on the right. My Work's greeting line (`DashboardGreeting`)
 *   scrolls with the board below this pinned header.
 * - `FreeToolbarChrome` — free dashboards (the workflow Dashboard tab and
 *   saved dashboard views):
 *   the full filter toolbar plus the widget controls, in the same below-tabs
 *   band as the board/list toolbars — its window pill on the LEFT too. The
 *   board refresh (`DashboardRefreshControl`) leads the right cluster, then
 *   the quick-filter chip (the shared `FilterPanel` over the screen's filter
 *   set — the view's own filters inside a saved view — present only where
 *   the host mounts an `IssueFilterProvider`) and Only-Me. The customize
 *   side is the Customize view gear and Add card (undo/redo are keyboard-
 *   only — Cmd+Z / Cmd+Shift+Z via the host's grid history hotkeys), and the
 *   host's save control (`SaveViewControl`) leads the row while shared
 *   edits are staged. The board is always editable for a permitted viewer —
 *   there is no edit mode.
 *
 * Personalization has no toolbar slot on free dashboards. Which board a
 * viewer sees is decided by fork state, and the board actions (Reset to team
 * layout, Edit team layout) live in the Customize view sections
 * (`dashboard-view-options.tsx`) behind the gear. Managed homes do not show
 * ownership controls.
 *
 * Both chromes hold at the top — they mount outside the board's scroller, so
 * the toolbar band (or My Work's page header) pins while the board travels
 * under it. These are presentation-only: the host owns every piece of state
 * and wires analytics into the callbacks it passes down.
 */

export function DashboardMastheadChrome({
  title,
  dateRange,
  onDateRangeChange,
  dateAudience,
  actions,
  connectedToTabs,
}: Omittable<{
  title: string;
  dateRange: DatePresetValue;
  onDateRangeChange: (next: DatePresetValue) => void;
  dateAudience: "personal" | "team";
  actions?: ReactNode;
  /**
   * This surface sits under a tab strip. The chrome then renders its scope
   * controls in the toolbar band below the tabs — the band every view's
   * toolbar shares — instead of a titled page header.
   */
  connectedToTabs?: boolean;
}>) {
  // A tab-connected managed home renders its controls in the toolbar band
  // below the tabs, not a titled page header — the same band the sibling
  // Dashboard tab's toolbar takes, so switching tabs on one entity never
  // shifts the chrome. On the shared band grammar the window pill is FRAMING
  // (it decides what every widget reports on — the board's Group By
  // analogue), so it takes the band's left slot, exactly where the sibling
  // Dashboard tab seats its own pill; the board's actions keep the right.
  // The selected tab already names the surface, so the band carries no
  // title, and the shell's breadcrumb line owns the mobile trigger.
  if (connectedToTabs) {
    return (
      <DataTableToolbar>
        <DataTableToolbarLeft>
          <ManagedHomeDateRangeSelect
            value={dateRange}
            onChange={onDateRangeChange}
            audience={dateAudience}
            align="start"
          />
        </DataTableToolbarLeft>
        <DataTableToolbarActions>{actions}</DataTableToolbarActions>
      </DataTableToolbar>
    );
  }

  // A standalone managed home (My Work) opens with the app's standard
  // first-line header — the title owns the left, so the scope controls live
  // right like every page header's actions. The window pill reads first (it
  // decides what every widget is showing), then the board's own actions
  // beside it — no rule between them: the chip and the filled Manage cards
  // action are one cluster (Spencer, 2026-09-25). Managed homes persist
  // edits without exposing board ownership controls.
  return (
    <Box background="background">
      <PageHeader
        title={title}
        actions={
          <Box direction="row" align="center" gap="sm">
            <ManagedHomeDateRangeSelect
              value={dateRange}
              onChange={onDateRangeChange}
              audience={dateAudience}
              align="end"
            />
            {actions}
          </Box>
        }
      />
      <Separator />
    </Box>
  );
}

/**
 * My Work's greeting line. It scrolls with the board (the pinned header above
 * already carries the title and controls) on the same centered 1200px column
 * as the widgets. It IS the page's h1, so it takes the page-title rung every
 * other page's `PageHeaderTitle` renders — one top size across the app.
 */
export function DashboardGreeting({ greeting }: { greeting: string }) {
  return (
    <Box direction="column" align="center" paddingX={{ base: "sm", md: "lg" }}>
      <Box fill="width" maxWidth={MANAGED_HOME_CONTENT_MAX_WIDTH} paddingTop="lg">
        <PageHeaderTitle>{greeting}</PageHeaderTitle>
      </Box>
    </Box>
  );
}

/**
 * The standalone managed home's page-layout door: a settings gear on the
 * quiet outline rung at the trailing edge of the masthead actions, opening a
 * popover with the page's own display options — today ONE Layout section
 * holding the "Page greeting" switch. These are properties of how the VIEWER
 * reads the page, never of the board, so the gear renders for every viewer
 * regardless of board edit rights, and no save control is involved: each
 * switch persists the instant it flips. The gear sits after "Manage cards"
 * behind its own vertical rule — board authoring first, page setup last.
 */
export function DashboardLayoutPopover({
  greetingVisible,
  onGreetingVisibleChange,
}: Omittable<{
  greetingVisible: boolean;
  onGreetingVisibleChange: (visible: boolean) => void;
}>) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  aria-label="Page layout"
                  size="icon-xs"
                  variant="outline"
                  shape="square"
                />
              }
            >
              <Icon name="settings" size="sm" />
            </PopoverTrigger>
          }
        />
        <TooltipPopup side="bottom">Customize your page layout</TooltipPopup>
      </Tooltip>
      {/* The option-panel section carries its own insets against the popover
          surface; the card rung holds one label/switch row per line. */}
      <PopoverPopup side="bottom" align="end" padding="none" width="card">
        <Box direction="column" paddingY="xs">
          <OptionPanelSection title="Layout">
            <OptionPanelRow label="Page greeting">
              <Switch
                checked={greetingVisible}
                onCheckedChange={(checked) => onGreetingVisibleChange(!!checked)}
              />
            </OptionPanelRow>
          </OptionPanelSection>
        </Box>
      </PopoverPopup>
    </Popover>
  );
}

/**
 * The band a fork-holding writer sees while on the "Edit team layout" jump —
 * their edits land on the SHARED board here, not their fork, and this is the
 * one place that says so. It mounts under the toolbar, outside the board's
 * scroller, and Done drops the viewer back onto their fork. Done disables
 * while staged edits are dirty: the toolbar's Save/Discard must resolve them
 * first, or leaving would silently drop the working copy.
 */
export function DashboardTeamLayoutBanner({
  scopeKind,
  dirty,
  onDone,
}: Omittable<{
  /** The host scope's kind — picks the shared board's entity icon. */
  scopeKind: IssueScope["kind"];
  /** Staged shared edits pending — Done disables until Save/Discard. */
  dirty: boolean;
  onDone: () => void;
}>) {
  const scopeIcon = scopeIsWorkflowBound(scopeKind)
    ? ENTITY_KIND_ICON.workflow
    : ENTITY_KIND_ICON.team;

  return (
    <Box background="background">
      <Box direction="row" align="center" gap="sm" paddingX="md" paddingY="xs">
        <Icon name={scopeIcon} size="sm" />
        <Box direction="column" grow>
          <Text size="sm" weight="medium">
            Editing the team layout
          </Text>
          <Text size="xs" color="muted">
            {dirty
              ? "Save or discard your changes to finish"
              : "Changes you save here apply for everyone"}
          </Text>
        </Box>
        <Button size="xs" variant="outline" disabled={dirty} onClick={onDone}>
          Done
        </Button>
      </Box>
      <Separator />
    </Box>
  );
}

/**
 * The Customize view mount for dashboards OUTSIDE a saved-view scope: with
 * no `ViewsProvider` the anchored `ViewOptionsPanel` cannot exist, so the
 * same `DashboardViewOptions` sections render in a popover behind the same
 * gear instead. One content component, two mounts.
 */
function DashboardOptionsPopover({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);

  // This popover and the filter chip's menu are both trigger-anchored popups
  // on one toolbar: pressing either trigger is an outside press for the
  // other, so they exclude each other with no effect wiring. The sections
  // carry no Filters row here — outside a saved-view scope there is no view
  // whose saved filters a Filters page could edit.
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger
          render={
            <PopoverTrigger
              render={
                <Button
                  aria-label="Customize view"
                  size="icon-xs"
                  variant="outline"
                  shape="pill"
                />
              }
            >
              <Icon name="settings" size="sm" />
            </PopoverTrigger>
          }
        />
        <TooltipPopup side="top">Customize your view settings</TooltipPopup>
      </Tooltip>
      {/* The option-panel sections carry their own insets against the popover
          surface; the panel width holds the same measure as the anchored
          view-options sheet so the rows read identically in both mounts. */}
      <PopoverPopup side="bottom" align="end" padding="none" className="w-[359px]">
        <Box direction="column" paddingY="xs">
          {children}
        </Box>
      </PopoverPopup>
    </Popover>
  );
}

export function FreeToolbarChrome({
  save,
  options,
  scope,
  canEdit,
  onAddWidget,
  refresh,
  consumeOnly = false,
  ...toolbarProps
}: Omit<
  DashboardFilterToolbarProps,
  "leading" | "children" | "refresh" | "filter"
> & {
  /** The staged-save control (`SaveViewControl`) — first in the row, so
   *  unsaved shared edits are answered before any other control. Renders
   *  nothing while the board is clean. */
  save: ReactNode;
  /** The dashboard's Customize view sections (`DashboardViewOptions`).
   *  Inside a saved-view scope the gear opens the anchored panel — the HOST
   *  mounts `ViewOptionsPanel sections={options}`; outside one the same node
   *  renders in this chrome's own popover behind the same gear. */
  options: ReactNode;
  /** The host scope — the filter panel's Simple page drops what the scope
   *  has already answered (a workflow-pinned board asks about stages, not
   *  workflows). */
  scope: IssueScope;
  canEdit: boolean;
  onAddWidget: () => void;
  /** The host's board-refresh handle (`useWidgetRefresh`): its `tick` keys
   *  the board so a press remounts every widget body, and its state drives
   *  the data-window cluster's leading control. */
  refresh: WidgetRefresh;
}) {
  const views = useViewsOptional();
  const hasViewScope = Boolean(views?.activeView);
  // The screen's filters — the view's own layer and the toolbar's temporary
  // pool, which the host AND-folds into every widget's entity scope. Null
  // outside an `IssueFilterProvider` (nothing would narrow), so the chip
  // renders only where it does work.
  const filterCtx = useIssueFiltersOptional();

  return (
    <DashboardFilterToolbar
      leading={save}
      refresh={<DashboardRefreshControl refresh={refresh} />}
      filter={
        filterCtx ? (
          // The panel renders the chip and hangs off it — the same mount the
          // board and list toolbars keep, so a second press on the chip
          // closes it and the filter hotkey has a target.
          <FilterPanel
            open={filterCtx.filterPanelOpen}
            onOpenChange={filterCtx.setFilterPanelOpen}
            config={filterCtx.config}
            filters={filterCtx.filters}
            scope={scope}
            viewFilters={
              views && filterCtx.viewFilterCount > 0
                ? {
                    count: filterCtx.viewFilterCount,
                    onEdit: () => views.openPanel("filters", "filter_chip"),
                  }
                : undefined
            }
            onClearAll={filterCtx.clearAll}
            trigger={
              // The chip counts BOTH layers and its × clears both; a view
              // filter cleared here is a view edit, so Save appears for it.
              <FilterToggle
                size="xs"
                icon="filter"
                count={filterCtx.activeFilterCount}
                onClear={() => filterCtx.clearAll("chip")}
                tooltip="Quickly filter your Projects"
                clearTooltip="Clear all filters"
              />
            }
          />
        ) : undefined
      }
      consumeOnly={consumeOnly}
      {...toolbarProps}
    >
      {/* The Customize view door (the gear, or the popover it falls back to)
          is the setup of the view; a consume-only reader has no setup. */}
      {consumeOnly ? null : hasViewScope ? (
        <ViewOptionsTrigger />
      ) : (
        <DashboardOptionsPopover>{options}</DashboardOptionsPopover>
      )}
      {canEdit ? (
        <Button size="xs" onClick={onAddWidget}>
          <Text size={null}>Add card</Text>
        </Button>
      ) : null}
    </DashboardFilterToolbar>
  );
}
